import React, { useState, useCallback, useMemo } from "react";
import { Tooltip } from "@material-ui/core";
import { useMobileDetection } from "../../utils/deviceDetection";
import { getActionCardColors } from "./ActionCardColors";
import MobileActionCardOverlay from "./MobileActionCardOverlay";
import ActionCardHeader from "./ActionCardHeader";
import ActionCardBody from "./ActionCardBody";

const ActionCardView = ({ 
  action, 
  styleCardSize, 
  onSelect, 
  canPlay,
  isSelected,
  isNew = false,
  isDiscardMode = false
}) => {
  const actionCardSize = useMemo(() => ({
    width: styleCardSize?.width || "150px",
    height: styleCardSize?.height || "210px"
  }), [styleCardSize]);

  const colors = useMemo(() => getActionCardColors(action), [action]);
  const [isHovered, setIsHovered] = useState(false);
  const isMobile = useMobileDetection();

  const handleClick = useCallback(() => {
    if (onSelect && canPlay) {
      onSelect(action);
    }
  }, [onSelect, canPlay, action]);

  const tooltipText = useMemo(() => {
    if (!canPlay) return "Not your turn or no actions remaining";
    if (isDiscardMode && isSelected) return "Click to remove from discard selection";
    if (isDiscardMode) return "Click to select for discard";
    if (isSelected) return "Click to deselect";
    return `Click to select: ${action.displayValue || action.name}`;
  }, [canPlay, isSelected, isDiscardMode, action.displayValue, action.name]);

  const handleMouseEnter = useCallback(() => setIsHovered(true), []);
  const handleMouseLeave = useCallback(() => setIsHovered(false), []);

  const cardStyle = useMemo(() => ({
    ...actionCardSize,
    background: colors.bg,
    ...(isSelected ? {} : { borderColor: colors.border }),
    height: actionCardSize.height
  }), [actionCardSize, colors.bg, colors.border, isSelected]);

  return (
    <Tooltip title={tooltipText} arrow placement="top">
      <div 
        className="action-card-container" 
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={!isMobile ? handleClick : undefined}
      >
      <div 
        className={`stock-card card action-card ${isNew ? 'card-new' : ''} ${isSelected && !isDiscardMode ? 'action-card-selected' : ''} ${isSelected && isDiscardMode ? 'action-card-discard-selected' : ''} ${!canPlay ? 'action-card-disabled' : ''}`} 
        style={cardStyle}
      >
        <ActionCardHeader 
          action={action} 
          colors={colors} 
          isMobile={isMobile}
          cardWidth={actionCardSize.width}
        />

        <ActionCardBody 
          action={action} 
          colors={colors} 
          isMobile={isMobile} 
        />

        {/* Mobile Overlay */}
        {isMobile && (
          <MobileActionCardOverlay
            actionCard={action}
            onSelect={handleClick}
            canPlay={canPlay}
            isSelected={isSelected}
          />
        )}

        {canPlay && isHovered && !isSelected && (
          <div className="action-card-hover-overlay">
            <div 
              className="action-card-select-button"
              style={{ background: isDiscardMode ? '#ef4444' : colors.border }}
            >
              {isDiscardMode ? 'DISCARD' : 'SELECT'}
            </div>
          </div>
        )}

        {isSelected && !isDiscardMode && (
          <div className="action-card-checkmark">
            ✓
          </div>
        )}

        {isSelected && isDiscardMode && (
          <div className="action-card-discard-mark">
            🗑️
          </div>
        )}
      </div>
      </div>
    </Tooltip>
  );
};

export default ActionCardView;

